import { Link } from 'react-router-dom';
import { usePageContent } from '@/context/SiteDataContext';
import GalleryRows from '@/components/GalleryRows';
import AdminHint from '@/components/editable/AdminHint';

/**
 * MainHistory (public)
 * --------------------
 * Timeline-driven history/story page for the MAIN Jurisdictional site.
 * Data lives at page_content.history:
 *   { hero_image, hero_video_url, hero_heading, hero_blurb,
 *     history_entries: [{ id, order, year, date, title, text, image, side }] }
 */
export default function MainHistory() {
  const c = usePageContent('history');
  const entries = (c.history_entries || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const heroImg = c.hero_image;
  const heroVideo = c.hero_video_url;

  const scrollTo = (id) => {
    const el = document.getElementById(`m-entry-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          {heroVideo ? (
            <video autoPlay muted loop playsInline data-testid="main-history-hero-video" className="w-full h-full object-cover">
              <source src={heroVideo} type="video/mp4" />
            </video>
          ) : (
            heroImg
              ? <img src={heroImg} alt="" className="w-full h-full object-cover" />
              : <img src="https://images.unsplash.com/photo-1519452575417-564c1401ecc0?w=2400" alt="" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-royal-dark/85 via-royal/60 to-royal-dark/90" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-white text-center">
          <div className="overline text-gold mb-3">Our Journey</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold gold-underline mx-auto inline-block">
            {c.hero_heading || 'Our History & Story'}
          </h1>
          <p className="mt-4 text-white/85 text-lg max-w-3xl mx-auto">
            {c.hero_blurb || 'From our chartering to today — the moments, mothers, and mile-markers of the Wisconsin First Ecclesiastical Jurisdiction.'}
          </p>
        </div>
      </section>

      {entries.length > 0 && (
        <section className="sticky top-0 z-30 bg-royal-dark/95 backdrop-blur border-b border-gold/30" data-testid="main-history-timeline">
          <div className="max-w-6xl mx-auto px-4 py-4 overflow-x-auto">
            <div className="flex items-center gap-3">
              {entries.map((e) => (
                <button key={e.id} onClick={() => scrollTo(e.id)}
                  data-testid={`main-timeline-year-${e.id}`}
                  className="group flex-shrink-0 flex items-center gap-2 bg-white/5 hover:bg-gold hover:text-royal-dark border border-white/20 hover:border-gold text-white rounded-lg px-3 py-1.5 transition">
                  {e.image && (
                    <div className="w-9 h-9 rounded overflow-hidden bg-white/10 flex-shrink-0">
                      <img src={e.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex flex-col items-start leading-tight">
                    <span className="font-serif text-base font-bold">{e.year || '—'}</span>
                    {(e.date || e.title) && <span className="text-[10px] opacity-80 group-hover:opacity-100 uppercase tracking-widest">{e.date || e.title}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6 space-y-24">
          {entries.length === 0 && (
            <AdminHint className="text-center py-16 text-charcoal-muted italic">
              No history entries yet. Add them in Admin ▸ History &amp; Story.
            </AdminHint>
          )}
          {entries.map((e) => {
            const imgOnLeft = e.side !== 'right';
            const photo = e.image ? (
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl ring-4 ring-gold/30">
                <img src={e.image} alt={e.title || ''} className="w-full h-full object-cover" />
              </div>
            ) : null;
            return (
              <div key={e.id} id={`m-entry-${e.id}`} data-testid={`m-entry-${e.id}`}
                   className="grid md:grid-cols-12 gap-8 items-center scroll-mt-24">
                <div className={`md:col-span-6 ${imgOnLeft ? 'md:order-1' : 'md:order-2'}`}>{photo}</div>
                <div className={`md:col-span-6 ${imgOnLeft ? 'md:order-2' : 'md:order-1'}`}>
                  <div className="overline text-gold mb-2">{e.year} {e.date && `· ${e.date}`}</div>
                  <h3 className="font-serif text-3xl text-royal font-bold mb-4 gold-underline">{e.title}</h3>
                  <p className="text-charcoal-muted text-lg leading-relaxed whitespace-pre-wrap">{e.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <GalleryRows scope="page:history" wrapClass="bg-[#F8F9FA] py-14" innerClass="max-w-6xl mx-auto px-6">
        <div className="overline mb-2">Photos</div>
        <h3 className="font-serif text-2xl text-royal font-bold mb-8 gold-underline">Moments Along the Way</h3>
      </GalleryRows>
    </div>
  );
}
